import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
    AddSurveyUrl,
    AddSurveyUrlFailure,
    AddSurveyUrlSuccess,
    ApproveSurvey,
    ApproveSurveyFailure,
    ApproveSurveySuccess,
    CompleteSurvey,
    CompleteSurveyFailure,
    CompleteSurveySuccess,
    GenerateProjectSetup,
    GenerateProjectSetupFailure,
    GenerateProjectSetupSuccess,
    LoadSurvey,
    LoadSurveyDescription,
    LoadSurveyDescriptionFailure,
    LoadSurveyDescriptionSuccess,
    LoadSurveyFailure,
    LoadSurveys,
    LoadSurveysFailure,
    LoadSurveysSuccess,
    LoadSurveySuccess,
    RejectSurvey,
    RejectSurveyFailure,
    RejectSurveySuccess
} from '../actions/hwp-survey.actions';
import { Store } from '@ngrx/store';
import { HealthyWorkplaceManagementControllerService, HealthyWorkplaceSurveyManagementDTO } from '@iota/inh-core-v1-management-client';

@Injectable()
export class HwpSurveyEffects {
    loadSurveys$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadSurveys),
            mergeMap(() =>
                this.healthyWorkplaceService.findAll().pipe(
                    map((surveys: HealthyWorkplaceSurveyManagementDTO[]) => LoadSurveysSuccess({ surveys })),
                    catchError((error) => of(LoadSurveysFailure({ error })))
                )
            )
        )
    );

    loadSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadSurvey),
            mergeMap((action) => {
                return this.healthyWorkplaceService.findSurvey(action.surveyId).pipe(
                    map((survey: HealthyWorkplaceSurveyManagementDTO) => LoadSurveySuccess({ survey })),
                    catchError((error) => of(LoadSurveyFailure({ error })))
                );
            })
        )
    );

    generateProjectSetup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GenerateProjectSetup),
            mergeMap((action) =>
                this.healthyWorkplaceService.generateProjectSetup(action.surveyId).pipe(
                    map((response: any) => {
                        let projectSetup: string[] = [];
                        projectSetup = response.split('\n');
                        return GenerateProjectSetupSuccess({ projectSetup });
                    }),
                    catchError((error) => of(GenerateProjectSetupFailure({ error })))
                )
            )
        )
    );

    approveSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ApproveSurvey),
            mergeMap((action) =>
                this.healthyWorkplaceService.approveSurvey(action.surveyId).pipe(
                    map((survey: HealthyWorkplaceSurveyManagementDTO) => ApproveSurveySuccess({ survey })),
                    catchError((error) => of(ApproveSurveyFailure({ error })))
                )
            )
        )
    );

    rejectSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RejectSurvey),
            mergeMap((action) =>
                this.healthyWorkplaceService.rejectSurvey(action.surveyId).pipe(
                    map((survey: HealthyWorkplaceSurveyManagementDTO) => RejectSurveySuccess({ survey })),
                    catchError((error) => of(RejectSurveyFailure({ error })))
                )
            )
        )
    );

    addSurveyUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AddSurveyUrl),
            mergeMap((action) =>
                this.healthyWorkplaceService.activateSurvey(action.surveyId, action.newUrl).pipe(
                    map((survey: HealthyWorkplaceSurveyManagementDTO) => AddSurveyUrlSuccess({ survey })),
                    catchError((error) => of(AddSurveyUrlFailure({ error })))
                )
            )
        )
    );

    completeSurvey$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompleteSurvey),
            mergeMap((action) =>
                this.healthyWorkplaceService.completeSurvey(action.surveyId, action.reportIds).pipe(
                    map((survey: HealthyWorkplaceSurveyManagementDTO) => CompleteSurveySuccess({ survey })),
                    catchError((error) => of(CompleteSurveyFailure({ error })))
                )
            )
        )
    );

    loadSurveyDescription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadSurveyDescription),
            mergeMap((action) =>
                this.healthyWorkplaceService.getConfigurationFile(action.surveyId).pipe(
                    // The generated signature says `string`, but the endpoint returns a spreadsheet and the
                    // request runs with `responseType: 'blob'`, so the value really is a Blob.
                    map((configurationFile: string) => {
                        return LoadSurveyDescriptionSuccess({ surveyDescription: configurationFile as unknown as Blob });
                    }),
                    catchError((error) => of(LoadSurveyDescriptionFailure({ error: error.message })))
                )
            )
        )
    );

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly healthyWorkplaceService: HealthyWorkplaceManagementControllerService
    ) {}
}
